import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Label,
  ConfirmDialog,
  ResultDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { walletsApi, companiesApi, blockchainNetworksApi } from "@/services/api";
import {
  Wallet,
  Plus,
  RefreshCw,
  ExternalLink,
  Copy,
  CheckCircle,
  Loader2,
  Star,
  MoreHorizontal,
  Pencil,
  Trash2,
  Power,
  Import,
  X,
} from "lucide-react";

interface WalletData {
  id: string;
  companyId: string | null;
  companyName?: string;
  networkId: string;
  networkName?: string;
  address: string;
  label?: string;
  purpose: "TREASURY" | "OPERATIONS" | "COLLECTION" | "ADMIN_MINTER";
  status: "ACTIVE" | "INACTIVE" | "COMPROMISED";
  isDefault: boolean;
  lastKnownBalanceWei?: string;
  lastKnownBalanceFormatted?: string;
  lastSyncedAt?: string;
  createdAt: string;
}

interface Company {
  id: string;
  razaoSocial: string;
}

interface Network {
  id: string;
  name: string;
  explorerUrl?: string;
  currencySymbol?: string;
}

interface FormData {
  companyId: string | null;
  networkId: string;
  label: string;
  purpose: "TREASURY" | "OPERATIONS" | "COLLECTION" | "ADMIN_MINTER";
  isDefault: boolean;
}

interface ImportFormData extends FormData {
  importType: "privateKey" | "mnemonic";
  privateKey: string;
  mnemonic: string;
  derivationPath: string;
}

const initialFormData: FormData = {
  companyId: null,
  networkId: "",
  label: "",
  purpose: "TREASURY",
  isDefault: false,
};

const purposeLabels: Record<string, string> = {
  TREASURY: "Treasury",
  OPERATIONS: "Operacoes",
  COLLECTION: "Arrecadacao",
  ADMIN_MINTER: "Admin Minter",
};

const purposeOptions = [
  { value: "TREASURY", label: "Treasury (Principal)" },
  { value: "OPERATIONS", label: "Operacoes" },
  { value: "COLLECTION", label: "Arrecadacao" },
];

export function WalletsPage() {
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState<string>(""); // "admin" | "empresa" | ""
  const [filterCompany, setFilterCompany] = useState<string>("");
  const [filterNetwork, setFilterNetwork] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWallet, setEditingWallet] = useState<WalletData | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [importFormData, setImportFormData] = useState<ImportFormData>({
    ...initialFormData,
    importType: "privateKey",
    privateKey: "",
    mnemonic: "",
    derivationPath: "m/44'/60'/0'/0/0",
  });
  const [formError, setFormError] = useState("");

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  // Result dialog state
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "error",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadWallets();
  }, [filterType, filterCompany, filterNetwork, filterStatus]);


  async function loadData() {
    try {
      const [companiesData, networksData] = await Promise.all([
        companiesApi.list(),
        blockchainNetworksApi.list(),
      ]);
      setCompanies(companiesData);
      setNetworks(networksData);
      await loadWallets();
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadWallets() {
    try {
      const filters: Record<string, string> = {};
      if (filterCompany) filters.companyId = filterCompany;
      if (filterNetwork) filters.networkId = filterNetwork;
      if (filterStatus) filters.status = filterStatus;

      let data = await walletsApi.list(
        Object.keys(filters).length > 0 ? filters : undefined
      );

      // Filtrar por tipo (admin vs empresa) no frontend
      if (filterType === "admin") {
        data = data.filter((w: WalletData) => !w.companyId);
      } else if (filterType === "empresa") {
        data = data.filter((w: WalletData) => !!w.companyId);
      }

      setWallets(data);
    } catch (error) {
      console.error("Erro ao carregar carteiras:", error);
    }
  }

  async function handleCreate() {
    setFormError("");

    if (!formData.companyId || !formData.networkId) {
      setFormError("Empresa e Rede sao obrigatorios");
      return;
    }

    setSaving(true);
    try {
      await walletsApi.create({
        ...formData,
        companyId: formData.companyId as string, // Já validamos que não é null acima
      });
      setShowCreateModal(false);
      setFormData(initialFormData);
      await loadWallets();
    } catch (error: any) {
      setFormError(error.message || "Erro ao criar carteira");
    } finally {
      setSaving(false);
    }
  }

  async function handleImport() {
    setFormError("");

    if (!importFormData.companyId || !importFormData.networkId) {
      setFormError("Empresa e Rede sao obrigatorios");
      return;
    }

    if (importFormData.importType === "privateKey" && !importFormData.privateKey) {
      setFormError("Private Key e obrigatoria");
      return;
    }

    if (importFormData.importType === "mnemonic" && !importFormData.mnemonic) {
      setFormError("Mnemonic e obrigatorio");
      return;
    }

    setSaving(true);
    try {
      const data: Record<string, unknown> = {
        companyId: importFormData.companyId as string, // Já validamos que não é null acima
        networkId: importFormData.networkId,
        label: importFormData.label || undefined,
        purpose: importFormData.purpose,
        isDefault: importFormData.isDefault,
      };

      if (importFormData.importType === "privateKey") {
        data.privateKey = importFormData.privateKey;
      } else {
        data.mnemonic = importFormData.mnemonic;
        if (importFormData.derivationPath) {
          data.derivationPath = importFormData.derivationPath;
        }
      }

      await walletsApi.import(data as Parameters<typeof walletsApi.import>[0]);
      setShowImportModal(false);
      setImportFormData({
        ...initialFormData,
        importType: "privateKey",
        privateKey: "",
        mnemonic: "",
        derivationPath: "m/44'/60'/0'/0/0",
      });
      await loadWallets();
    } catch (error: any) {
      setFormError(error.message || "Erro ao importar carteira");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!editingWallet) return;
    setFormError("");

    setSaving(true);
    try {
      await walletsApi.update(editingWallet.id, {
        label: formData.label || undefined,
        purpose: formData.purpose,
      });
      setShowEditModal(false);
      setEditingWallet(null);
      await loadWallets();
    } catch (error: any) {
      setFormError(error.message || "Erro ao atualizar");
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncBalance(id: string) {
    setSyncing(id);
    try {
      await walletsApi.syncBalance(id);
      await loadWallets();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao sincronizar",
        description: error instanceof Error ? error.message : "Não foi possível sincronizar o saldo. Tente novamente.",
      });
    } finally {
      setSyncing(null);
    }
  }

  async function handleSetDefault(id: string) {
    try {
      await walletsApi.setDefault(id);
      await loadWallets();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao definir padrão",
        description: error instanceof Error ? error.message : "Não foi possível definir a carteira como padrão. Tente novamente.",
      });
    }
  }

  async function handleToggleStatus(wallet: WalletData) {
    try {
      await walletsApi.update(wallet.id, {
        status: wallet.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      });
      await loadWallets();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao alterar status",
        description: error instanceof Error ? error.message : "Não foi possível alterar o status da carteira. Tente novamente.",
      });
    }
  }

  function handleDelete(id: string) {
    setConfirmDialog({
      open: true,
      title: "Excluir Carteira",
      description:
        "ATENCAO: Esta acao e irreversivel. A carteira sera permanentemente removida do sistema (as chaves serao perdidas).",
      confirmText: "Excluir",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await walletsApi.delete(id);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          await loadWallets();
        } catch (error) {
          console.error("Erro ao excluir:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function copyAddress(address: string) {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  }

  function truncateAddress(address: string) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  function getExplorerUrl(address: string, networkId: string) {
    const network = networks.find((n) => n.id === networkId);
    if (!network?.explorerUrl) return null;
    return `${network.explorerUrl}/address/${address}`;
  }

  function openEditModal(wallet: WalletData) {
    setEditingWallet(wallet);
    setFormData({
      companyId: wallet.companyId,
      networkId: wallet.networkId,
      label: wallet.label || "",
      purpose: wallet.purpose,
      isDefault: wallet.isDefault,
    });
    setFormError("");
    setShowEditModal(true);
  }

  const stats = [
    { value: wallets.length, label: "Total" },
    { value: wallets.filter((w) => w.status === "ACTIVE").length, label: "Ativas" },
    { value: wallets.filter((w) => w.status === "INACTIVE").length, label: "Inativas" },
  ];

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Carteiras"
          description="Gerenciamento de carteiras blockchain das empresas"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Carteiras"
        description="Gerenciamento de carteiras blockchain das empresas"
        stats={stats}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setImportFormData({
                  ...initialFormData,
                  importType: "privateKey",
                  privateKey: "",
                  mnemonic: "",
                  derivationPath: "m/44'/60'/0'/0/0",
                });
                setFormError("");
                setShowImportModal(true);
              }}
              className="bg-white text-gray-700 hover:bg-gray-50"
            >
              <Import className="w-4 h-4 mr-2" />
              Importar
            </Button>
            <Button
              onClick={() => {
                setFormData(initialFormData);
                setFormError("");
                setShowCreateModal(true);
              }}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Carteira
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 flex-wrap">
            <select
              className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                if (e.target.value === "admin") {
                  setFilterCompany(""); // Limpar filtro de empresa se selecionou admin
                }
              }}
            >
              <option value="">Todas</option>
              <option value="admin">Admin (Sistema)</option>
              <option value="empresa">Empresas</option>
            </select>

            {filterType !== "admin" && (
              <select
                className="flex h-10 w-64 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
              >
                <option value="">Todas as empresas</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial}
                  </option>
                ))}
              </select>
            )}

            <select
              className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filterNetwork}
              onChange={(e) => setFilterNetwork(e.target.value)}
            >
              <option value="">Todas as redes</option>
              {networks.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>

            <select
              className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="ACTIVE">Ativo</option>
              <option value="INACTIVE">Inativo</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Wallets List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Carteiras Cadastradas</CardTitle>
        </CardHeader>
        <CardContent>
          {wallets.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nenhuma carteira encontrada.
            </p>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Empresa</th>
                    <th className="p-4 font-medium">Endereco</th>
                    <th className="p-4 font-medium">Rede</th>
                    <th className="p-4 font-medium">Saldo</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {wallets.map((wallet) => {
                    const explorerUrl = getExplorerUrl(wallet.address, wallet.networkId);
                    const network = networks.find((n) => n.id === wallet.networkId);

                    return (
                      <tr
                        key={wallet.id}
                        className="border-b border-border last:border-0 hover:bg-muted/50"
                      >
                        <td className="p-4">
                          <div>
                            <div className="flex items-center gap-2">
                              {wallet.companyId ? (
                                <span className="font-medium">{wallet.companyName}</span>
                              ) : (
                                <span className="font-medium text-purple-600">Admin (Sistema)</span>
                              )}
                              {wallet.isDefault && (
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Badge
                                variant={wallet.purpose === "ADMIN_MINTER" ? "default" : "outline"}
                                className={`text-xs ${wallet.purpose === "ADMIN_MINTER" ? "bg-purple-100 text-purple-700" : ""}`}
                              >
                                {purposeLabels[wallet.purpose]}
                              </Badge>
                              {wallet.label && <span>{wallet.label}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                              {truncateAddress(wallet.address)}
                            </code>
                            {explorerUrl && (
                              <a
                                href={explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="Ver no Explorer"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => copyAddress(wallet.address)}
                              className="text-muted-foreground hover:text-foreground"
                              title="Copiar endereco"
                            >
                              {copiedAddress === wallet.address ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm">{wallet.networkName}</span>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            {wallet.lastKnownBalanceFormatted ? (
                              <span>
                                {parseFloat(wallet.lastKnownBalanceFormatted).toFixed(4)}{" "}
                                {network?.currencySymbol || "XDC"}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge
                            variant={
                              wallet.status === "ACTIVE"
                                ? "success"
                                : wallet.status === "COMPROMISED"
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {wallet.status === "ACTIVE"
                              ? "Ativa"
                              : wallet.status === "COMPROMISED"
                              ? "Comprometida"
                              : "Inativa"}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSyncBalance(wallet.id)}
                              disabled={syncing === wallet.id}
                              title="Sincronizar saldo"
                            >
                              {syncing === wallet.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[160px]">
                                <DropdownMenuItem onClick={() => openEditModal(wallet)}>
                                  <Pencil className="w-3 h-3 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                {!wallet.isDefault && (
                                  <DropdownMenuItem onClick={() => handleSetDefault(wallet.id)}>
                                    <Star className="w-3 h-3 mr-2" />
                                    Definir como Padrao
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleToggleStatus(wallet)}>
                                  <Power className="w-3 h-3 mr-2" />
                                  {wallet.status === "ACTIVE" ? "Desativar" : "Ativar"}
                                </DropdownMenuItem>
                                {!wallet.isDefault && (
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(wallet.id)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="w-3 h-3 mr-2" />
                                    Excluir
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Nova Carteira</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="companyId">Empresa *</Label>
                <select
                  id="companyId"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.companyId || ""}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value || null })}
                >
                  <option value="">Selecione a empresa</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razaoSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="networkId">Rede Blockchain *</Label>
                <select
                  id="networkId"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.networkId}
                  onChange={(e) => setFormData({ ...formData, networkId: e.target.value })}
                >
                  <option value="">Selecione a rede</option>
                  {networks.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="label">Label (opcional)</Label>
                <Input
                  id="label"
                  placeholder="Carteira de Operacoes"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="purpose">Proposito *</Label>
                <select
                  id="purpose"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.purpose}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      purpose: e.target.value as FormData["purpose"],
                    })
                  }
                >
                  {purposeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData({ ...formData, isDefault: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium">
                  Definir como carteira padrao da empresa
                </span>
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Criar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Importar Carteira Existente</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowImportModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="import-companyId">Empresa *</Label>
                <select
                  id="import-companyId"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={importFormData.companyId || ""}
                  onChange={(e) =>
                    setImportFormData({ ...importFormData, companyId: e.target.value || null })
                  }
                >
                  <option value="">Selecione a empresa</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razaoSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-networkId">Rede Blockchain *</Label>
                <select
                  id="import-networkId"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={importFormData.networkId}
                  onChange={(e) =>
                    setImportFormData({ ...importFormData, networkId: e.target.value })
                  }
                >
                  <option value="">Selecione a rede</option>
                  {networks.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-type">Metodo de Importacao *</Label>
                <select
                  id="import-type"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={importFormData.importType}
                  onChange={(e) =>
                    setImportFormData({
                      ...importFormData,
                      importType: e.target.value as "privateKey" | "mnemonic",
                    })
                  }
                >
                  <option value="privateKey">Private Key</option>
                  <option value="mnemonic">Mnemonic (12 ou 24 palavras)</option>
                </select>
              </div>

              {importFormData.importType === "privateKey" ? (
                <div className="space-y-2">
                  <Label htmlFor="privateKey">Private Key *</Label>
                  <Input
                    id="privateKey"
                    type="password"
                    placeholder="0x..."
                    value={importFormData.privateKey}
                    onChange={(e) =>
                      setImportFormData({ ...importFormData, privateKey: e.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    A chave sera criptografada antes de armazenar
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="mnemonic">Mnemonic *</Label>
                    <Input
                      id="mnemonic"
                      type="password"
                      placeholder="word1 word2 word3..."
                      value={importFormData.mnemonic}
                      onChange={(e) =>
                        setImportFormData({ ...importFormData, mnemonic: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="derivationPath">Derivation Path (opcional)</Label>
                    <Input
                      id="derivationPath"
                      placeholder="m/44'/60'/0'/0/0"
                      value={importFormData.derivationPath}
                      onChange={(e) =>
                        setImportFormData({
                          ...importFormData,
                          derivationPath: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="import-label">Label (opcional)</Label>
                <Input
                  id="import-label"
                  placeholder="Carteira Importada"
                  value={importFormData.label}
                  onChange={(e) =>
                    setImportFormData({ ...importFormData, label: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-purpose">Proposito *</Label>
                <select
                  id="import-purpose"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={importFormData.purpose}
                  onChange={(e) =>
                    setImportFormData({
                      ...importFormData,
                      purpose: e.target.value as FormData["purpose"],
                    })
                  }
                >
                  {purposeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowImportModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleImport} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Importar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingWallet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Editar Carteira</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-address">Endereco</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="edit-address"
                    value={editingWallet.address}
                    disabled
                    className="bg-muted"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyAddress(editingWallet.address)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-label">Label</Label>
                <Input
                  id="edit-label"
                  placeholder="Treasury Principal"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-purpose">Proposito</Label>
                <select
                  id="edit-purpose"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.purpose}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      purpose: e.target.value as FormData["purpose"],
                    })
                  }
                >
                  {purposeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
